const LOJA_VIRTUAL_ENTIDADE_ID_STORAGE_KEY = 'loja_virtual_entidade_id'
const ACCOUNT_PROFILE_STORAGE_KEY = 'account-profile'
const COOKIE_ENTITY_ID_KEYS = [
  'b2b_account_entity_id',
  'comprador_entidade_id',
  'compradorentidadeid',
  'cliente_entidade_id',
  'clienteentidadeid',
  'loja_virtual_entidade_id',
  'entidade_id',
] as const

let lojaVirtualEntidadeId: number | null = null
let lojaVirtualEntidadeIdHydrated = false

function normalizeEntidadeId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.trunc(parsed)
    }
  }

  return undefined
}

function hydrateLojaVirtualEntidadeId() {
  if (lojaVirtualEntidadeIdHydrated && lojaVirtualEntidadeId !== null) return
  lojaVirtualEntidadeIdHydrated = true

  if (typeof window === 'undefined') return

  try {
    const stored = window.localStorage.getItem(LOJA_VIRTUAL_ENTIDADE_ID_STORAGE_KEY)
    const normalized = normalizeEntidadeId(stored)
    if (normalized) {
      lojaVirtualEntidadeId = normalized
      return
    }
  } catch {
    // Ignore localStorage read errors.
  }

  const fallbackEntidadeId = readFallbackEntidadeId()
  if (!fallbackEntidadeId) return

  lojaVirtualEntidadeId = fallbackEntidadeId

  try {
    window.localStorage.setItem(LOJA_VIRTUAL_ENTIDADE_ID_STORAGE_KEY, String(fallbackEntidadeId))
  } catch {
    // Ignore localStorage write errors.
  }
}

function readFallbackEntidadeId(): number | undefined {
  return readEntidadeIdFromPersistedAccountProfile() ?? readEntidadeIdFromCookies()
}

function readEntidadeIdFromPersistedAccountProfile(): number | undefined {
  if (typeof window === 'undefined') return undefined

  try {
    const rawValue = window.localStorage.getItem(ACCOUNT_PROFILE_STORAGE_KEY)
    if (!rawValue) return undefined

    const parsed = JSON.parse(rawValue) as { entidadeId?: unknown; entidade_id?: unknown } | null
    if (!parsed || typeof parsed !== 'object') return undefined

    return normalizeEntidadeId(parsed.entidadeId ?? parsed.entidade_id)
  } catch {
    return undefined
  }
}

function readEntidadeIdFromCookies(): number | undefined {
  if (typeof document === 'undefined') return undefined

  const cookieEntries = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)

  for (const entry of cookieEntries) {
    const separatorIndex = entry.indexOf('=')
    const rawKey = separatorIndex >= 0 ? entry.slice(0, separatorIndex).trim() : entry.trim()
    const rawValue = separatorIndex >= 0 ? entry.slice(separatorIndex + 1).trim() : ''
    if (!rawKey || !rawValue) continue

    const normalizedKey = rawKey.toLocaleLowerCase('pt-BR')
    if (!COOKIE_ENTITY_ID_KEYS.includes(normalizedKey as typeof COOKIE_ENTITY_ID_KEYS[number])) continue

    const normalizedValue = normalizeEntidadeId(decodeURIComponent(rawValue))
    if (normalizedValue) return normalizedValue
  }

  return undefined
}

export function getAuthenticatedEntidadeId(): number | undefined {
  hydrateLojaVirtualEntidadeId()
  return lojaVirtualEntidadeId ?? undefined
}

export function setAuthenticatedEntidadeId(entidadeId?: number | null): void {
  const normalized = normalizeEntidadeId(entidadeId)

  lojaVirtualEntidadeIdHydrated = true
  lojaVirtualEntidadeId = normalized ?? null

  if (typeof window === 'undefined') return

  try {
    if (typeof normalized === 'number') {
      window.localStorage.setItem(LOJA_VIRTUAL_ENTIDADE_ID_STORAGE_KEY, String(normalized))
    } else {
      window.localStorage.removeItem(LOJA_VIRTUAL_ENTIDADE_ID_STORAGE_KEY)
    }
  } catch {
    // Ignore localStorage write errors.
  }
}

export function clearAuthenticatedEntidadeId(): void {
  setAuthenticatedEntidadeId(null)
}
